import type { ResourceRef, Signal } from '@angular/core';
import { type PortfolioViewerQuery, type UserWithProgramRolesQueryQuery } from '@reconcept-platform/shared/types';
import { type GraphQLResult, isType, type OfType, type ValueOfTypename } from '@reconcept-platform/shared/util';
import type { Observable } from 'rxjs';
import { type SelectableOptionGroup } from '../schema/forms/selectable-option';
import { assertNotNull, isNotNull } from './nullable.utils';

export { isType };
export type { GraphQLResult, OfType, ValueOfTypename };

export interface GraphQLResultWithId extends GraphQLResult {
  id: string;
}

export interface IdFromType<T extends GraphQLResultWithId> {
  __typename: T['__typename'];
  id: T['id'];
}

export type Viewer = PortfolioViewerQuery['viewer'];
// @ts-expect-error TS2344 type constraint not satisfied
export type UserWithProgramRoles = OfType<UserWithProgramRolesQueryQuery['node'], 'User'>;

export type Require<T, K extends keyof T> = T & { [P in K]-?: T[P] };

// can't use keyof , which will only give common fields of a Union Type
// this helps get all the keys
export type KeysOfUnion<T> = T extends any ? keyof T : never;

export type TypePick<T extends GraphQLResultWithId, K extends keyof T> = {
  [Key in K]: T[Key];
} & { __typename: T['__typename']; id: T['id'] };

/**
 * Narrows to the typename, null when missing or another type. Never throws.
 * Only where null is a valid answer of the query itself, like an optional relation or a union member you don't need.
 */
export function typeOrNull<Result extends GraphQLResult, Typename extends ValueOfTypename<Result>>(
  result: Result | null | undefined,
  typename: Typename,
): OfType<Result, Typename> | null {
  if (!result) {
    return null;
  }

  return isType(result, typename) ? result : null;
}

/**
 * `assertType` on `result.data.node`, for every `node(id:)`, watchQuery included.
 * Read a watcher through `rxResource`: when the node is deleted or no longer accessible, the throw puts the
 * resource in its error state for the template's error branch, instead of reaching the ErrorHandler.
 */
export function assertGraphqlDataNode<Result extends GraphQLResult, Typename extends ValueOfTypename<Result>>(
  result: { data: { node?: Result | null } },
  typename: Typename,
): OfType<Result, Typename> {
  return assertType(result.data.node, typename);
}

/**
 * Narrows to the typename, throws when missing or another type.
 * Use it where the value must be there; `typeOrNull` where null is a valid answer.
 */
export function assertType<Result extends GraphQLResult, Typename extends ValueOfTypename<Result>>(
  result: Result | null | undefined,
  typename: Typename,
): OfType<Result, Typename> {
  return assertNotNull(typeOrNull(result, typename));
}

// keeps the nodes of the typename and drops the rest, never throws
export function assertGraphqlDataNodes<Result extends GraphQLResult, Typename extends ValueOfTypename<Result>>(
  result: { data: { nodes?: (Result | null)[] | null } },
  typename: Typename,
): OfType<Result, Typename>[] {
  return (result.data.nodes ?? []).filter((node): node is OfType<Result, Typename> =>
    isNotNull(node) ? isType(node, typename) : false,
  );
}

export function newRecord<X extends string | number | symbol, Y>(): Record<X, Y> {
  return {} as Record<X, Y>;
}

/**
 * Helper type to pick specific fields from a nested object
 *
 * @example
 * Pick<Portfolio, 'id' | 'status'> &
 *   PickNested<Portfolio, 'portfolioFormat', 'shortName' | 'name'>;
 * will result in:
 * {
 *  id: string;
 *  status: string;
 *  portfolioFormat: {
 *  shortName: string;
 *  name: string;
 *}
 *
 * powered by ChatGPT
 */
// the nested field is nullable in the schema; the subfields describe the value when it is there,
// so the field itself keeps the optionality it has on the schema type
export type PickNested<T, K extends keyof T, SubFields extends keyof NonNullable<T[K]>> = {
  [P in K]: Pick<NonNullable<T[K]>, SubFields>;
};

export type WithNodes<Type, FieldName extends string> = {
  [P in FieldName]: { nodes: Type[] };
};

export type WithOptionalNodes<Type, FieldName extends string> = {
  [P in FieldName]: { nodes?: Type[] };
};

/**
 * Same idea as WithOptionalNodes, but now the key itself is optional
 */
export type WithOptionalOptionalNodes<Type, FieldName extends string> = {
  [P in FieldName]?: { nodes?: Type[] };
};

/**
 * Utility type to extract the return type of an Angular resource
 */
export type SignalTypeOf<R extends Signal<unknown>> = R extends Signal<infer T> ? T : never;
export type ResourceTypeOf<R extends ResourceRef<unknown>> = R extends ResourceRef<infer T> ? T : never;
export type ObservableTypeOf<O extends Observable<unknown>> = O extends Observable<infer T> ? T : never;
export type SelectableOptionGroupTypeOf<SOG extends SelectableOptionGroup<unknown>> =
  SOG extends SelectableOptionGroup<infer T> ? T : never;
